/**
 * La lista de proveedores, con lo que se le debe a cada uno.
 *
 * LA FILA ENTERA NAVEGA, Y POR ESO NO LLEVA BOTONES
 * -------------------------------------------------
 * El MASTER §6 lo dice así: «cursor pointer solo si la fila realmente navega, y
 * entonces no lleva además un botón dentro de la primera celda (doble objetivo,
 * doble foco)». La versión anterior tenía el saldo como botón y el lápiz al
 * final: tres objetivos por fila para una sola intención. Ahora la fila lleva a
 * la ficha y editar vive adentro de ella.
 *
 * EL TOTAL VA EN EL PIE, STICKY
 * -----------------------------
 * Una fila por moneda y con chip, porque las dos monedas no se suman nunca. El
 * `TableFooter` del primitivo ya es sticky; lo que hace falta es que la tabla
 * tenga `alto="acotado"`, porque un sticky sin contenedor con techo no se pega
 * a nada. Antes el total estaba en un `Card` después de la tabla, que es
 * exactamente lo que hay que scrollear para leer.
 *
 * EL TÉCNICO NO ES UN PROVEEDOR
 * -----------------------------
 * Va en su propia fila, ARRIBA de las demás, y lleva a la liquidación en lote
 * del taller. Iba al final: con la tabla acotada quedaba escondida en el
 * scroll interno, y el pie decía «Total que debo US$ 180» sin ninguna fila a
 * la vista que lo explicara. Aparece acá porque «cuánto debo» es una sola
 * pregunta, y tenerla repartida en dos pantallas es lo que hacía que nadie la
 * contestara de memoria.
 */
import { Link } from "@tanstack/react-router";
import { Truck, Wrench } from "lucide-react";

import type { Moneda } from "@tora/shared";

import { ChipMoneda } from "@/components/money";
import { Importe } from "@/components/money/importe";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { aNumero } from "@/lib/tipos";

import { haceCuanto, hayDeuda, textoAntiguedad, totalDeuda, type SaldoProveedor } from "./logica";

/** La misma tolerancia que el FIFO en la base. */
const CENTAVO = 0.009;

export interface DeudaConElTecnico {
  porMoneda: Partial<Record<Moneda, number>>;
  ordenes: number;
}

export interface ListaProveedoresProps {
  filas: ReadonlyArray<SaldoProveedor>;
  /** El total en la base, para saber si quedó algo afuera. */
  total: number;
  deudaTecnico: DeudaConElTecnico;
  cargando?: boolean;
  onVerMas?: () => void;
}

export function ListaProveedores({
  filas,
  total,
  deudaTecnico,
  cargando = false,
  onVerMas,
}: ListaProveedoresProps): JSX.Element {
  if (filas.length === 0) {
    return cargando ? (
      <Card padding="none">
        <Cargando texto="Buscando…" />
      </Card>
    ) : (
      <EstadoVacio
        icono={Truck}
        titulo="Sin proveedores cargados"
        detalle="También se crean solos cuando cargás un remito y el que figura ahí todavía no existe."
      />
    );
  }

  const suma = totalDeuda(filas);
  const tecnicoArs = deudaTecnico.porMoneda.ARS ?? 0;
  const tecnicoUsd = deudaTecnico.porMoneda.USD ?? 0;
  const leDebeAlTecnico = deudaTecnico.ordenes > 0;

  const totalArs = suma.ars + tecnicoArs;
  const totalUsd = suma.usd + tecnicoUsd;

  const pie = (
    <PieDeLista
      visibles={filas.length}
      total={total}
      sustantivo="proveedores"
      {...(onVerMas ? { onVerMas } : {})}
      cargando={cargando}
    />
  );

  return (
    <>
      {/* Desktop: tabla. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        <Table densidad="compacta" alto="acotado">
          <TableHeader>
            <TableRow>
              <TableHead>Proveedor</TableHead>
              <TableHead>Lista en</TableHead>
              <TableHead>Contacto</TableHead>
              {/* Sin `aria-sort`: la columna todavía no ordena, y anunciar un
                  orden que nadie puede cambiar es una promesa vacía. */}
              <TableHead className="text-right">Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leDebeAlTecnico ? (
              <FilaTecnico ars={tecnicoArs} usd={tecnicoUsd} ordenes={deudaTecnico.ordenes} />
            ) : null}
            {filas.map((p) => (
              <FilaProveedor key={p.id} proveedor={p} />
            ))}
          </TableBody>
          <TableFooter>
            {Math.abs(totalUsd) > CENTAVO ? (
              <TableRow>
                <TableCell colSpan={3}>Total que debo, en dólares</TableCell>
                <TableCell className="text-right">
                  <Importe monto={totalUsd} moneda="USD" />
                </TableCell>
              </TableRow>
            ) : null}
            {Math.abs(totalArs) > CENTAVO ? (
              <TableRow>
                <TableCell colSpan={3}>Total que debo, en pesos</TableCell>
                <TableCell className="text-right">
                  <Importe monto={totalArs} moneda="ARS" />
                </TableCell>
              </TableRow>
            ) : null}
          </TableFooter>
        </Table>
        {pie}
      </Card>

      {/* Mobile: una tarjeta-registro por proveedor. */}
      <div className="flex flex-col gap-2 md:hidden">
        {leDebeAlTecnico ? (
          <TarjetaTecnico ars={tecnicoArs} usd={tecnicoUsd} ordenes={deudaTecnico.ordenes} />
        ) : null}
        {filas.map((p) => (
          <TarjetaProveedor key={p.id} proveedor={p} />
        ))}
        <Card
          padding="compacto"
          variant="muted"
          className="flex items-center justify-between gap-3"
        >
          <span className="text-sm font-medium">Total que debo</span>
          <ParDeMonedas ars={totalArs} usd={totalUsd} />
        </Card>
        {pie}
      </div>
    </>
  );
}

function FilaProveedor({ proveedor: p }: { proveedor: SaldoProveedor }): JSX.Element {
  const antiguedad = textoAntiguedad(p.antiguedad_dias);
  const contacto = [p.contacto, p.telefono].filter((v): v is string => v !== null);

  return (
    <TableRow className="cursor-pointer">
      <TableCell>
        {/* El único objetivo de la fila. Editar vive adentro de la ficha. */}
        <Link to="/proveedores" search={{ id: p.id }} className="block">
          <span className="font-medium">{p.nombre}</span>
          {!p.activo ? (
            <Badge variant="outline" className="ml-2">
              De baja
            </Badge>
          ) : null}
        </Link>
      </TableCell>
      <TableCell>
        <ChipMoneda moneda={p.moneda_lista} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {contacto.length > 0 ? contacto.join(" · ") : "—"}
      </TableCell>
      <TableCell className="text-right">
        {hayDeuda(p) ? (
          <span className="flex flex-col items-end">
            <ParDeMonedas ars={aNumero(p.saldo_ars)} usd={aNumero(p.saldo_usd)} />
            {antiguedad ? (
              <span className="text-xs text-muted-foreground">{haceCuanto(p.antiguedad_dias)}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function FilaTecnico({
  ars,
  usd,
  ordenes,
}: {
  ars: number;
  usd: number;
  ordenes: number;
}): JSX.Element {
  return (
    <TableRow className="cursor-pointer">
      <TableCell colSpan={3}>
        <Link
          to="/reparaciones"
          search={{ liquidar: true }}
          className="flex items-center gap-2 text-sm"
        >
          <Wrench aria-hidden="true" className="size-4 text-muted-foreground" />
          Técnico (taller)
          <span className="text-xs text-muted-foreground">
            {ordenes === 1 ? "1 orden" : `${ordenes} órdenes`}
          </span>
        </Link>
      </TableCell>
      <TableCell className="text-right">
        <ParDeMonedas ars={ars} usd={usd} />
      </TableCell>
    </TableRow>
  );
}

/**
 * Tarjeta-registro (MASTER §8): barra lateral de moneda, importe arriba a la
 * derecha, y toda la tarjeta es el objetivo porque navega.
 */
function TarjetaProveedor({ proveedor: p }: { proveedor: SaldoProveedor }): JSX.Element {
  const contacto = [p.contacto, p.telefono].filter((v): v is string => v !== null);
  const antiguedad = textoAntiguedad(p.antiguedad_dias);

  return (
    <Card as="article" padding="compacto" acento={p.moneda_lista}>
      <Link
        to="/proveedores"
        search={{ id: p.id }}
        className="flex items-start justify-between gap-3"
      >
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{p.nombre}</span>
            {!p.activo ? <Badge variant="outline">De baja</Badge> : null}
          </div>
          {contacto.length > 0 ? (
            <p className="truncate text-xs tabular-nums text-muted-foreground">
              {contacto.join(" · ")}
            </p>
          ) : null}
          {/* `haceCuanto` y no «hace {antiguedad}»: con una deuda del día decía
              «hace hoy». */}
          {antiguedad ? (
            <p className="text-xs text-muted-foreground">{haceCuanto(p.antiguedad_dias)}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {hayDeuda(p) ? (
            <ParDeMonedas ars={aNumero(p.saldo_ars)} usd={aNumero(p.saldo_usd)} />
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
          <ChipMoneda moneda={p.moneda_lista} />
        </div>
      </Link>
    </Card>
  );
}

function TarjetaTecnico({
  ars,
  usd,
  ordenes,
}: {
  ars: number;
  usd: number;
  ordenes: number;
}): JSX.Element {
  return (
    <Card as="article" padding="compacto">
      <Link
        to="/reparaciones"
        search={{ liquidar: true }}
        className="flex items-start justify-between gap-3"
      >
        <span className="flex items-center gap-2 text-sm">
          <Wrench aria-hidden="true" className="size-4 text-muted-foreground" />
          Técnico (taller)
          <span className="text-xs text-muted-foreground">
            {ordenes === 1 ? "1 orden" : `${ordenes} órdenes`}
          </span>
        </span>
        <ParDeMonedas ars={ars} usd={usd} />
      </Link>
    </Card>
  );
}

/** Dos importes, uno por moneda, y solo los que tienen algo. Nunca sumados. */
function ParDeMonedas({ ars, usd }: { ars: number; usd: number }): JSX.Element {
  const hayArs = Math.abs(ars) > CENTAVO;
  const hayUsd = Math.abs(usd) > CENTAVO;

  return (
    <span className="flex flex-col items-end">
      {hayUsd ? <Importe monto={usd} moneda="USD" tamano="sm" /> : null}
      {hayArs ? <Importe monto={ars} moneda="ARS" tamano="sm" /> : null}
      {!hayArs && !hayUsd ? <span className="text-sm text-muted-foreground">—</span> : null}
    </span>
  );
}
